import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaMinusCircle } from 'react-icons/fa';
import { getProgrammePrimaryTitle } from '../../utils/programmeDisplay';
import {
  CLASSIFICATION_BADGE_WIDTH_CLASS,
  CLASSIFICATION_BADGE_TEXT_CLASS,
  getClassificationBadgeDisplay,
  getClassificationLoadingBadgeDisplay,
  JUPAS_HIGH_RISK_ACTION_CLASS,
  isRequirementFulfillmentAwaiting,
  REQUIREMENT_BADGE_SIZE_CLASS,
  resolveRequirementBadgeValue,
  resolveRequirementFulfillmentDisplay,
} from '../../utils/jupasClassificationDisplay';
import {
  getInstitutionLabel,
  ProgrammeMetaFooter,
  ProgrammeScoreSummary,
  ProgrammeStatsGrid,
} from '../../utils/jupasProgrammeDisplay';
import CollapsibleSlide from '../ui/CollapsibleSlide';
import { JUPAS_BAND_COLORS } from '../../utils/jupasOrderDisplay';
import {
  JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS,
  JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS,
  JUPAS_PROGRAMME_BAR_IDENTITY_CLASS,
  JUPAS_PROGRAMME_BAR_MAIN_CLASS,
  JUPAS_PROGRAMME_BAR_ROW_CLASS,
  JUPAS_PROGRAMME_BAR_TITLE_CLASS,
  JUPAS_PROGRAMME_BAR_TRAILING_CLASS,
  JUPAS_PROGRAMME_CODE_LABEL_CLASS,
} from '../../utils/jupasLayoutDisplay';
import MobileProgrammeSwipeAction from './MobileProgrammeSwipeAction';

const JupasProgrammeCard = ({
  programme,
  rank,
  bandLabel,
  bandColor = JUPAS_BAND_COLORS.A,
  preferShortName = false,
  classificationData,
  classification,
  classificationLoading = false,
  isExpanded = false,
  defaultExpanded = false,
  showInlineDetails = true,
  isSelected = false,
  onToggle,
  onRemove,
  showDragHandle = false,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  isDragging = false,
  dragShiftY = 0,
  className = '',
  scoreDataPending = false,
  statsYear,
}) => {
  const { t, i18n } = useTranslation();
  const showDetails = showInlineDetails && (isExpanded || defaultExpanded);
  const resolvedClassification = classificationData?.classification ?? classification;
  const classificationPending = classificationLoading || resolvedClassification == null;
  const badgeDisplay = classificationPending
    ? getClassificationLoadingBadgeDisplay(t)
    : getClassificationBadgeDisplay(resolvedClassification, t);
  const fulfillmentDisplay = resolveRequirementFulfillmentDisplay(
    resolveRequirementBadgeValue(classificationData),
    t,
    {
      loading: classificationLoading,
      awaiting: isRequirementFulfillmentAwaiting(classificationData, { classificationLoading }),
    }
  );
  const programmeTitle = preferShortName && programme.short_name
    ? programme.short_name
    : getProgrammePrimaryTitle(programme, i18n.language);

  const handleHeaderClick = () => {
    onToggle?.(programme);
  };

  const handleNativeDragStart = (event) => {
    // Block Chromium's default element/text drag so clicks open details as on Safari.
    if (!draggable) {
      event.preventDefault();
      return;
    }
    onDragStart?.(event);
  };

  return (
    <MobileProgrammeSwipeAction
      enabled={Boolean(onRemove)}
      tone="remove"
      actionLabel={t('jupa.remove')}
      onAction={() => onRemove?.(programme)}
      onTap={onToggle ? handleHeaderClick : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      allowOverflow={showDragHandle && (isDragging || dragShiftY !== 0)}
      className={`rounded-lg ${showDragHandle ? 'md:overflow-visible' : ''}`}
    >
      <div
        draggable={draggable}
        onDragStart={handleNativeDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        style={{
          transform: `translateY(${dragShiftY}px)`,
          transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
          ...(draggable ? {} : { WebkitUserDrag: 'none' }),
        }}
        className={`relative w-full max-w-full overflow-hidden rounded-lg border bg-surface shadow-sm transition hover:bg-surface-muted ${
          isSelected
            ? 'border-primary bg-primary/10 ring-1 ring-inset ring-primary/30'
            : 'border-edge'
        } ${isDragging ? 'opacity-0' : ''} ${className}`}
      >
      <div
        role={onToggle ? 'button' : undefined}
        tabIndex={onToggle ? 0 : undefined}
        onClick={onToggle && !onPointerDown ? handleHeaderClick : undefined}
        onKeyDown={onToggle ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleHeaderClick();
          }
        } : undefined}
        className={`${JUPAS_PROGRAMME_BAR_ROW_CLASS} ${
          showDragHandle
            ? 'cursor-grab touch-none active:cursor-grabbing'
            : onToggle
              ? 'cursor-pointer focus-visible:outline-none focus-visible:bg-surface-muted'
              : ''
        }`}
      >
        {rank != null ? (
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold leading-none text-neutral-900 sm:h-8 sm:w-8 sm:text-xs"
            style={{ backgroundColor: bandColor }}
          >
            {rank}
          </div>
        ) : null}
        {bandLabel ? (
          <span className="shrink-0 rounded-md bg-surface-muted px-2.5 py-1.5 text-[13px] font-medium text-ink-muted">
            {bandLabel}
          </span>
        ) : null}
        <div className={JUPAS_PROGRAMME_BAR_MAIN_CLASS}>
          <div className={JUPAS_PROGRAMME_BAR_IDENTITY_CLASS}>
            <span className={JUPAS_PROGRAMME_CODE_LABEL_CLASS}>
              {programme.code}
            </span>
            <div className={JUPAS_PROGRAMME_BAR_TITLE_CLASS}>
              <p className="truncate text-sm font-medium text-ink">
                {programmeTitle}
              </p>
              <p className="mt-0.5 truncate text-xs text-ink-subtle">
                {getInstitutionLabel(programme.institution, t, i18n.language)}
              </p>
            </div>
          </div>
        </div>
        <div className={JUPAS_PROGRAMME_BAR_TRAILING_CLASS}>
          {fulfillmentDisplay ? (
            <span
              className={`inline-flex ${REQUIREMENT_BADGE_SIZE_CLASS} shrink-0 items-center justify-center rounded-full font-bold leading-none ${fulfillmentDisplay.className}`}
              title={fulfillmentDisplay.label}
              aria-label={fulfillmentDisplay.label}
              role={fulfillmentDisplay.isLoading ? 'status' : undefined}
            >
              {fulfillmentDisplay.isLoading ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
              ) : (
                fulfillmentDisplay.code
              )}
            </span>
          ) : null}
          {badgeDisplay ? (
            <span
              className={`inline-flex ${CLASSIFICATION_BADGE_WIDTH_CLASS} ${CLASSIFICATION_BADGE_TEXT_CLASS} shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded px-1 py-1.5 font-medium sm:px-1.5 ${badgeDisplay.className}`}
              role={badgeDisplay.isLoading ? 'status' : undefined}
              title={badgeDisplay.label}
            >
              <span className="truncate">{badgeDisplay.label}</span>
            </span>
          ) : null}
          {onRemove ? (
            <div className="hidden md:block">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(programme);
                }}
                className={`${JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS} ${JUPAS_HIGH_RISK_ACTION_CLASS}`}
                aria-label={t('jupa.remove')}
                title={t('jupa.remove')}
              >
                <span className={`${JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS} group-hover:bg-red-50 group-active:bg-red-100`}>
                  <FaMinusCircle className="h-4 w-4" aria-hidden />
                </span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <CollapsibleSlide open={showDetails}>
        <div className="border-t border-edge px-4 py-4">
          <ProgrammeScoreSummary
            programme={programme}
            classificationData={classificationData}
            t={t}
            language={i18n.language}
            scoreDataPending={scoreDataPending}
          />
          <ProgrammeStatsGrid
            programme={programme}
            classificationData={classificationData}
            t={t}
            year={statsYear}
          />
          <ProgrammeMetaFooter programme={programme} t={t} language={i18n.language} />
        </div>
      </CollapsibleSlide>
      </div>
    </MobileProgrammeSwipeAction>
  );
};

export default JupasProgrammeCard;
